import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { subAffParentService } from "../../../services/admin/subAffParent.service";
import { OrderType } from "../../../components/affiliate/Trackers";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";

export type payloadUpdateParent = {
  affiliateId: number;
  action: 'assignParentAffiliate' | 'removeParentAffiliate';
  newParentId: number | null;
  validFrom: string | null;
};

export type payloadParentOptions = {
  affiliateId: number;
  page: number;
  perPage: number;
  search: string | null;
};

export type payloadParentHistoryList = {
  affiliateId: number;
  page: number;
  perPage: number;
  order: OrderType;
};

export type payloadUpdateParentHistoryRow = {
  id: number;
  action: 'assignParentAffiliate';
  newParentId: number;
  validFrom: string;
};

export type ParentAffiliateData = {
  id: number;
  username: string;
  assignedAt: string;
};

export type ParentHistoryItem = {
  id: number;
  createdAt: string;
  subAffiliateHierarchyChange?: {
    previousParentId: number | null;
    newParentId: number | null;
    newParentUsername: string | null;
    validFrom: string;
    status: string;
    createdById: number;
    createdByUsername: string | null;
    isEditable: boolean;
  } | null;
};

export type subAffParentSliceState = {
  updateParent: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  parentOptions: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  history: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  updateHistoryRow: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  deleteHistoryRow: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: subAffParentSliceState = {
  updateParent: {
    data: null,
    error: null,
    isFetching: false,
  },
  parentOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
  history: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateHistoryRow: {
    data: null,
    error: null,
    isFetching: false,
  },
  deleteHistoryRow: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const updateParentData: any = createAsyncThunk(
  'subAffParent/updateParentData',
  async (payload: payloadUpdateParent, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffParentService.updateParent(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getParentOptionsData: any = createAsyncThunk(
  'subAffParent/getParentOptionsData',
  async (payload: payloadParentOptions, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffParentService.getParentOptions(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getHistoryData: any = createAsyncThunk(
  'subAffParent/getHistoryData',
  async (payload: payloadParentHistoryList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await subAffParentService.getHistory(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateHistoryRowData: any = createAsyncThunk(
  'subAffParent/updateHistoryRowData',
  async (payload: payloadUpdateParentHistoryRow, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffParentService.updateHistoryRow(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const deleteHistoryRowData: any = createAsyncThunk(
  'subAffParent/deleteHistoryRowData',
  async (payload: { id: number | string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await subAffParentService.deleteHistoryRow(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.success',
          })
        );
      }

      if (!response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: !!data?.error ? data?.error : 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const subAffParentSlice = createSlice({
  name: 'subAffParent',
  initialState: initialState,
  reducers: {
    clearUpdateParentState(state) {
      state.updateParent.error = null;
      state.updateParent.isFetching = false;
      state.updateParent.data = null;
    },
    clearParentOptionsState(state) {
      state.parentOptions.error = null;
      state.parentOptions.isFetching = false;
      state.parentOptions.data = null;
    },
    clearHistoryState(state) {
      state.history.error = null;
      state.history.isFetching = false;
      state.history.data = null;
    },
    clearUpdateHistoryRowState(state) {
      state.updateHistoryRow.error = null;
      state.updateHistoryRow.isFetching = false;
      state.updateHistoryRow.data = null;
    },
    clearDeleteHistoryRowState(state) {
      state.deleteHistoryRow.error = null;
      state.deleteHistoryRow.isFetching = false;
      state.deleteHistoryRow.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<subAffParentSliceState>>) => {
    builder.addCase(updateParentData.pending, (state ) => {
      state.updateParent.isFetching = true;
      state.updateParent.error = null;
    });
    builder.addCase(updateParentData.fulfilled, (state , action) => {
      state.updateParent.error = null;
      state.updateParent.isFetching = false;
      state.updateParent.data = action.payload;
    });
    builder.addCase(updateParentData.rejected, (state , action) => {
      state.updateParent.isFetching = false;
      state.updateParent.error = action.payload;
      state.updateParent.data = null;
    });

    builder.addCase(getParentOptionsData.pending, (state ) => {
      state.parentOptions.isFetching = true;
      state.parentOptions.error = null;
    });
    builder.addCase(getParentOptionsData.fulfilled, (state , action) => {
      state.parentOptions.error = null;
      state.parentOptions.isFetching = false;
      state.parentOptions.data = action.payload;
    });
    builder.addCase(getParentOptionsData.rejected, (state , action) => {
      state.parentOptions.isFetching = false;
      state.parentOptions.error = action.payload;
      state.parentOptions.data = null;
    });

    builder.addCase(getHistoryData.pending, (state ) => {
      state.history.isFetching = true;
      state.history.error = null;
    });
    builder.addCase(getHistoryData.fulfilled, (state , action) => {
      state.history.error = null;
      state.history.isFetching = false;
      state.history.data = action.payload;
    });
    builder.addCase(getHistoryData.rejected, (state , action) => {
      state.history.isFetching = false;
      state.history.error = action.payload;
      state.history.data = null;
    });

    builder.addCase(updateHistoryRowData.pending, (state ) => {
      state.updateHistoryRow.isFetching = true;
      state.updateHistoryRow.error = null;
    });
    builder.addCase(updateHistoryRowData.fulfilled, (state , action) => {
      state.updateHistoryRow.error = null;
      state.updateHistoryRow.isFetching = false;
      state.updateHistoryRow.data = action.payload;
    });
    builder.addCase(updateHistoryRowData.rejected, (state , action) => {
      state.updateHistoryRow.isFetching = false;
      state.updateHistoryRow.error = action.payload;
      state.updateHistoryRow.data = null;
    });

    builder.addCase(deleteHistoryRowData.pending, (state ) => {
      state.deleteHistoryRow.isFetching = true;
      state.deleteHistoryRow.error = null;
    });
    builder.addCase(deleteHistoryRowData.fulfilled, (state , action) => {
      state.deleteHistoryRow.error = null;
      state.deleteHistoryRow.isFetching = false;
      state.deleteHistoryRow.data = action.payload;
    });
    builder.addCase(deleteHistoryRowData.rejected, (state , action) => {
      state.deleteHistoryRow.isFetching = false;
      state.deleteHistoryRow.error = action.payload;
      state.deleteHistoryRow.data = null;
    });
  },
});

export const {
  clearUpdateParentState,
  clearParentOptionsState,
  clearHistoryState,
  clearUpdateHistoryRowState,
  clearDeleteHistoryRowState,
} = subAffParentSlice.actions;

export default subAffParentSlice.reducer;
